/**
 * RE-MINT POLICY FOR AN EXPIRED ANONYMOUS SESSION.
 *
 * ── The defect this exists to close (measured in production 2026-09-05) ─────
 * `authenticatedFetch` recovered from a 401 by refreshing FIREBASE. An
 * anonymous visitor on the open app has no Firebase session, so recovery
 * returned the 401 unchanged. Their credential is the demo token, which
 * carries a 24h TTL and was minted exactly once, at boot, by `bootAsGuest()`.
 *
 * A tab left open overnight therefore came back to a dashboard whose three
 * data panels 401 forever. Observed on the wire, one client, ~5-minute poll:
 *
 *     08:18   429 401 401 401 429 429 429 429
 *     08:23   401 401 401 401 401 429 429 429 429
 *
 * That second half is the part this module is shaped by: FOUR gated requests
 * fire per dashboard load, so a naive "re-mint on 401" would issue four mints
 * and trip `rateLimitMiddleware("auth")` -- 10/min per IP. The fix must not
 * become the storm.
 *
 * ⚠️ Scope it honestly: the 13,275 refusals measured over 3.4 days came from
 * essentially ONE stale tab, not a population of visitors. This is a latent
 * scaling defect, not a live outage. It is fixed because it is real, not
 * because it is currently costing traffic.
 *
 * ── Why this is a LEAF module ──────────────────────────────────────────────
 * It imports nothing. `query-client.ts` pulls in Firebase and react-query,
 * which cannot load in the node test runner -- so a behavioural test of the
 * policy would be impossible if the policy lived there, and the guard next
 * door (`signInNeverBlocked.test.ts`) had to fall back to asserting on source
 * TEXT. Source assertions cannot see single-flight. Keeping the policy pure
 * is what makes the real behaviour testable.
 */

export const REMINT_COOLDOWN_MS = 60_000;

export interface AnonymousRemintPolicy {
  /**
   * True when a fresh token was obtained and the caller should retry ONCE.
   * False means give up and surface the original refusal.
   */
  shouldRetryAfterRemint(): Promise<boolean>;
  /** Test seam. */
  reset(): void;
  /** Test seam: how many times the underlying mint was actually invoked. */
  mintCount(): number;
}

export function createAnonymousRemintPolicy(opts: {
  mint: () => Promise<string | null>;
  now?: () => number;
  cooldownMs?: number;
}): AnonymousRemintPolicy {
  const now = opts.now ?? (() => Date.now());
  const cooldownMs = opts.cooldownMs ?? REMINT_COOLDOWN_MS;

  let inflight: Promise<string | null> | null = null;
  let coolUntil = 0;
  let mints = 0;

  return {
    async shouldRetryAfterRemint(): Promise<boolean> {
      if (now() < coolUntil) return false;

      // SINGLE-FLIGHT: every concurrent 401 awaits the same mint, so one
      // dashboard load costs one mint rather than four.
      if (!inflight) {
        mints += 1;
        inflight = opts
          .mint()
          .catch(() => null) // `mintDemoToken` never throws, but a registered
          .finally(() => {   // reminter is injected -- do not trust that here.
            inflight = null;
          });
      }

      const token = await inflight;
      if (token) return true;

      // The mint FAILED (offline, 429, server error). Stop asking for a while
      // so a dead tab cannot hammer the auth limiter on every poll cycle.
      coolUntil = now() + cooldownMs;
      return false;
    },
    reset() {
      inflight = null;
      coolUntil = 0;
      mints = 0;
    },
    mintCount() {
      return mints;
    },
  };
}
